import { useState, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useProfile } from '../context/ProfileContext';
import { F } from '../styles/theme';
import { useT } from '../i18n/useT';
import RedButton from '../components/red/RedButton';
import { RedSymbol, RedWordmark } from '../components/red/Brand';

const EASE = 'cubic-bezier(.16,1,.3,1)';

// Real SVG flag for DE/AT — emoji flags don't render in native UI on Windows.
function Flag({ code, w = 42 }) {
  const bands =
    code === 'DE' ? ['#000000', '#DD0000', '#FFCE00'] :
    code === 'AT' ? ['#C8102E', '#FFFFFF', '#C8102E'] : ['#888', '#aaa', '#888'];
  const h = Math.round(w * 0.7);
  return (
    <svg width={w} height={h} viewBox="0 0 30 21" aria-hidden
      style={{ display: 'block', borderRadius: 4, boxShadow: '0 0 0 1px rgba(0,0,0,0.10)' }}>
      {bands.map((c, i) => <rect key={i} x="0" y={i * 7} width="30" height="7" fill={c} />)}
    </svg>
  );
}

// Linien-Icons für die Quellen-Methoden (identisch zum Wizard-MethodStep).
const SRC_ICON = {
  api:    <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07L11.83 5" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
  wallet: <><rect x="2" y="6" width="20" height="14" rx="2" /><path d="M16 14a2 2 0 1 1 0-4h6v4z" /><path d="M2 8V6a2 2 0 0 1 2-2h14" /></>,
  csv:    <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" /></>,
  manual: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>,
};

// Onboarding-Wizard „aus einem Guss" (Mockup: brand/redesign-showcase.html):
// EIN durchgehender 4-Schritt-Fluss Name → Land → Quelle → Los mit einer oben
// mitwachsenden Progress-Linie. Hanken Grotesk, Koralle nur Akzent, kein Glow.
// Der frühere separate „Erste Quelle"-Screen ist jetzt Schritt 3.
export default function OnboardingScreen({ user, onConnectSource }) {
  const { T } = useTheme();
  const { t, lang } = useT();
  const { setName, setCountry, setOnboardingComplete, setFirstSourceSeen } = useProfile();

  // Only DE/AT are supported for now — localized labels via Intl.DisplayNames.
  const { labelDE, labelAT } = useMemo(() => {
    let dn;
    try { dn = new Intl.DisplayNames([lang], { type: 'region' }); } catch { dn = null; }
    const labelFor = (code) => {
      if (!dn) return code;
      try { return dn.of(code) || code; } catch { return code; }
    };
    return { labelDE: labelFor('DE'), labelAT: labelFor('AT') };
  }, [lang]);

  const [step, setStep] = useState(1);
  const [nameInput, setNameInput] = useState(
    user?.displayName ? user.displayName.split(' ')[0] : ''
  );
  const [savedName, setSavedName] = useState('');
  const [countryCode, setCountryCode] = useState('DE'); // 'DE' | 'AT'

  const TOTAL = 4;
  const handleNameNext = () => {
    if (!nameInput.trim()) return;
    const n = nameInput.trim();
    setName(n); setSavedName(n); setStep(2);
  };
  const handleCountryNext = () => { setCountry(countryCode); setStep(3); };
  // Quelle gewählt → Onboarding abschliessen, Erst-Quelle als gesehen markieren
  // und direkt in den echten Flow (api/utxowallet/csv/manual) springen.
  const pickSource = (m) => { setFirstSourceSeen(true); setOnboardingComplete(true); onConnectSource?.(m); };
  const skipSource = () => setStep(4);
  const handleFinish = () => { setFirstSourceSeen(true); setOnboardingComplete(true); };

  const en = lang === 'en';
  const kickerStep =
    step === 1 ? (en ? 'Welcome' : 'Willkommen') :
    step === 2 ? (en ? 'Step 2 of 4' : 'Schritt 2 von 4') :
    step === 3 ? (en ? 'Step 3 of 4' : 'Schritt 3 von 4') :
                 (en ? 'All set' : 'Fertig');

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '15px 16px',
    background: T.surfaceHi || T.surface, border: `1px solid ${T.line}`, borderRadius: 15,
    fontSize: 16, fontWeight: 600, color: T.text, fontFamily: F.family, outline: 'none',
  };

  const Kicker = (
    <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.red, fontWeight: 700, fontFamily: F.family }}>{kickerStep}</div>
  );
  const Title = ({ children }) => (
    <h2 style={{ margin: '8px 0 0', fontFamily: F.display, fontSize: 27, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.12, color: T.text }}>{children}</h2>
  );
  const Sub = ({ children }) => (
    <div style={{ fontSize: 14, color: T.sub, lineHeight: 1.5, marginTop: 10, fontFamily: F.family }}>{children}</div>
  );
  const Art = ({ children }) => (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 0 32px' }}>{children}</div>
  );
  // 2×2-Markenmotiv als roter Faden: leer beim Start (Step 1) → komplett mit Häkchen am Ende (Step 4).
  const ArtName = (
    <svg width="124" height="124" viewBox="0 0 132 132" fill="none" aria-hidden="true">
      <rect x="26" y="26" width="34" height="34" rx="9" stroke={T.line} strokeWidth="2.2" />
      <rect x="72" y="26" width="34" height="34" rx="9" stroke={T.line} strokeWidth="2.2" />
      <rect x="26" y="72" width="34" height="34" rx="9" stroke={T.line} strokeWidth="2.2" />
      <rect x="72" y="72" width="34" height="34" rx="9" fill={T.red} />
      <circle cx="89" cy="89" r="7" stroke="#fff" strokeWidth="2.4" />
      <path d="M82 102 a9 8 0 0 1 14 0" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
  const ArtCountry = (
    <svg width="116" height="116" viewBox="0 0 132 132" fill="none" aria-hidden="true">
      <circle cx="66" cy="66" r="44" stroke={T.line} strokeWidth="2.2" />
      <path d="M22 66 h88 M66 22 c16 15 16 73 0 88 c-16 -15 -16 -73 0 -88" stroke={T.line} strokeWidth="2.2" />
      <circle cx="66" cy="48" r="7" fill={T.red} />
    </svg>
  );
  const ArtDone = (
    <svg width="124" height="124" viewBox="0 0 132 132" fill="none" aria-hidden="true">
      <rect x="26" y="26" width="34" height="34" rx="9" stroke={T.line} strokeWidth="2.2" />
      <rect x="72" y="26" width="34" height="34" rx="9" stroke={T.line} strokeWidth="2.2" />
      <rect x="26" y="72" width="34" height="34" rx="9" stroke={T.line} strokeWidth="2.2" />
      <rect x="72" y="72" width="34" height="34" rx="9" fill={T.red} />
      <path d="M80 89 l6 6 l12 -13" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );

  const firstName = (savedName || user?.displayName?.split(' ')[0] || '').trim();

  return (
    <div style={{
      minHeight: '100vh', background: T.bg,
      display: 'flex', justifyContent: 'center', fontFamily: F.family,
    }}>
      <div style={{
        width: '100%', maxWidth: 460,
        padding: 'calc(env(safe-area-inset-top, 0px) + 30px) 28px calc(env(safe-area-inset-bottom, 0px) + 30px)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Wortmarke + durchgehende Progress-Linie (ein Journey-Gefühl) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 30 }}>
          <RedWordmark size={17} />
          <div style={{ flex: 1, height: 3, borderRadius: 99, background: T.line, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(step / TOTAL) * 100}%`, background: T.red, borderRadius: 99, transition: `width 0.5s ${EASE}` }} />
          </div>
        </div>

        {/* Inhalt — top-ausgerichtet, gleiche Schablone je Schritt */}
        <div style={{ paddingTop: 4 }}>
          {Kicker}

          {step === 1 && (
            <>
              <Title>{t('auth_name_title')}</Title>
              <Sub>{t('auth_name_sub')}</Sub>
              <Art>{ArtName}</Art>
              <input
                style={inputStyle}
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleNameNext()}
                placeholder={t('auth_name_placeholder')}
                autoFocus
              />
              <div style={{ marginTop: 18 }}>
                <RedButton variant="primary" size="lg" fullWidth onClick={handleNameNext} disabled={!nameInput.trim()}>
                  {t('next')}
                </RedButton>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Title>{t('auth_country_title')}</Title>
              <Sub>{t('auth_country_sub')}</Sub>
              <Art>{ArtCountry}</Art>
              <div style={{ display: 'flex', gap: 12 }}>
                {[
                  { code: 'DE', label: labelDE, sub: t('auth_country_de_sub') },
                  { code: 'AT', label: labelAT, sub: t('auth_country_at_sub') },
                ].map(({ code, label, sub }) => {
                  const selected = countryCode === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setCountryCode(code)}
                      style={{
                        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                        gap: 10, padding: '20px 12px', cursor: 'pointer', fontFamily: F.family,
                        background: T.surface, borderRadius: 16, position: 'relative',
                        border: `1.5px solid ${selected ? T.text : T.line}`,
                        transition: 'border-color .15s ease',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      {selected && (
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden
                          style={{ position: 'absolute', top: 11, right: 11 }}>
                          <path d="M3 8.5l3 3 7-7.5" stroke={T.red} strokeWidth="2"
                            fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      <Flag code={code} w={42} />
                      <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{label}</div>
                      <div style={{ fontSize: 11, color: T.sub, lineHeight: 1.3, textAlign: 'center' }}>{sub}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: 18 }}>
                <RedButton variant="primary" size="lg" fullWidth onClick={handleCountryNext}>
                  {t('next')}
                </RedButton>
              </div>
              <div style={{ marginTop: 8 }}>
                <RedButton variant="text" fullWidth onClick={() => setStep(1)}>{t('back')}</RedButton>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <Title>{firstName ? t('cfs_title', { name: firstName }) : t('cfs_title_noname')}</Title>
              <Sub>{t('cfs_sub')}</Sub>
              <div style={{ marginTop: 22 }}>
                {[
                  { id: 'api',        icon: SRC_ICON.api,    title: t('cfs_exchange_title'), sub: t('cfs_exchange_sub') },
                  { id: 'utxowallet', icon: SRC_ICON.wallet, title: t('cfs_wallet_title'),   sub: t('cfs_wallet_sub') },
                  { id: 'csv',        icon: SRC_ICON.csv,    title: t('cfs_csv_title'),      sub: t('cfs_csv_sub') },
                  { id: 'manual',     icon: SRC_ICON.manual, title: t('cfs_manual_title'),   sub: t('cfs_manual_sub') },
                ].map((m, i, arr) => (
                  <button
                    key={m.id}
                    onClick={() => pickSource(m.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, width: '100%',
                      padding: '16px 4px', textAlign: 'left', cursor: 'pointer',
                      background: 'transparent', border: 'none',
                      borderTop: `1px solid ${T.line}`,
                      borderBottom: i === arr.length - 1 ? `1px solid ${T.line}` : 'none',
                      color: T.text, WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                      stroke={T.sub} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
                      style={{ flexShrink: 0 }}>
                      {m.icon}
                    </svg>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: F.family, fontSize: 15.5, fontWeight: 700, letterSpacing: '-0.015em', color: T.text }}>{m.title}</div>
                      <div style={{ fontSize: 12, color: T.sub, lineHeight: 1.4, marginTop: 2 }}>{m.sub}</div>
                    </div>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                      stroke={T.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M10 4.8 L15.6 12 L10 19.2" />
                    </svg>
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 16 }}>
                <RedButton variant="text" fullWidth onClick={skipSource}>{t('cfs_skip') || t('skip') || 'Später'}</RedButton>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <Title>{t('auth_welcome_title')} {firstName}!</Title>
              <Sub>{t('auth_welcome_sub')}</Sub>
              <Art>{ArtDone}</Art>
              <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5, fontFamily: F.family, marginBottom: 4 }}>
                {t('auth_welcome_privacy')}
              </div>
              <div style={{ marginTop: 18 }}>
                <RedButton variant="primary" size="lg" fullWidth onClick={handleFinish}>
                  {t('auth_lets_go')}
                </RedButton>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
